import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import { ButtonComponent, OutlineButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { NwFilterOption, NwFilterSection } from './filter-drawer.types';

export type NwAppliedFilters = Record<string, string[]>;

@Component({
    selector: 'nw-filters-drawer',
    standalone: true,
    templateUrl: './filters-drawer.component.html',
    styleUrls: ['./filters-drawer.component.scss'],
    imports: [IconComponent, ButtonComponent, OutlineButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FiltersDrawerComponent implements OnChanges {
    @Input() open = false;
    @Input() title = 'Filters';
    @Input() sections: NwFilterSection[] = [];
    @Input() selectedValues: NwAppliedFilters = {};

    @Output() closed = new EventEmitter<void>();
    @Output() clear = new EventEmitter<void>();
    @Output() apply = new EventEmitter<NwAppliedFilters>();

    draftValues: NwAppliedFilters = {};
    expandedSections: Record<string, boolean> = {};

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedValues']) {
            this.draftValues = this.cloneFilters(this.selectedValues);
        }

        if (changes['sections']) {
            this.expandedSections = this.sections.reduce(
                (accumulator, section) => {
                    accumulator[section.id] = section.expanded ?? true;
                    return accumulator;
                },
                {} as Record<string, boolean>
            );
        }
    }

    isSectionExpanded(sectionId: string): boolean {
        return this.expandedSections[sectionId] ?? true;
    }

    toggleSection(sectionId: string): void {
        this.expandedSections[sectionId] = !this.isSectionExpanded(sectionId);
    }

    isSelected(sectionId: string, value: string): boolean {
        return (this.draftValues[sectionId] ?? []).includes(value);
    }

    get hasSelectedFilters(): boolean {
        return Object.values(this.draftValues).some((values) => values.length > 0);
    }

    get hasChanges(): boolean {
        const normalize = (filters: NwAppliedFilters): string => {
            const normalized = Object.keys(filters)
                .sort()
                .reduce((accumulator, key) => {
                    accumulator[key] = [...filters[key]].sort();
                    return accumulator;
                }, {} as NwAppliedFilters);
            return JSON.stringify(normalized);
        };
        return normalize(this.draftValues) !== normalize(this.selectedValues);
    }

    onCheckboxChange(sectionId: string, value: string, checked: boolean): void {
        const currentValues = this.draftValues[sectionId] ?? [];

        this.draftValues = {
            ...this.draftValues,
            [sectionId]: checked
                ? [...currentValues, value]
                : currentValues.filter((currentValue) => currentValue !== value)
        };
    }

    onRadioChange(sectionId: string, value: string): void {
        this.draftValues = {
            ...this.draftValues,
            [sectionId]: [value]
        };
    }

    onClear(): void {
        this.draftValues = {};
        this.clear.emit();
    }

    onApply(): void {
        this.apply.emit(this.cloneFilters(this.draftValues));
    }

    onClose(): void {
        this.closed.emit();
    }

    trackBySection(section: NwFilterSection): string {
        return section.id;
    }

    trackByOption(option: NwFilterOption): any {
        return option.value;
    }

    private cloneFilters(filters: NwAppliedFilters): NwAppliedFilters {
        return Object.entries(filters).reduce((accumulator, [key, values]) => {
            accumulator[key] = [...values];
            return accumulator;
        }, {} as NwAppliedFilters);
    }
}
